import { useEffect, useMemo, useState } from 'react'
import { Recipe, RecipeDifficulty, DIETARY_TAGS, DIETARY_LABEL_KEYS, useT } from '@reciperoll/core'

export type RecipeFilter = {
  key: string
  /** Display label for dynamic pills (tags/dietary). Static pills set `labelKey`. */
  label: string
  /** i18n key for the static filter labels; dynamic pills fall back to `label`. */
  labelKey?: string
  predicate: (r: Recipe) => boolean
}

// cookingDuration is stored in SECONDS (see duration-utils / selectQuickRecipes).
const UNDER_15_MIN_SECONDS = 15 * 60

// Single source of truth for the recipe-listing filters. Attribute filters
// (duration, difficulty) and dietary filters are unified — add one here and it
// appears on every listing screen.
export const RECIPE_FILTERS: RecipeFilter[] = [
  {
    key: 'under-15',
    label: 'Under 15 min',
    labelKey: 'library.filterUnder15',
    predicate: (r) => (r.cookingDuration ?? Infinity) <= UNDER_15_MIN_SECONDS,
  },
  {
    key: 'easy',
    label: 'Easy',
    labelKey: 'library.filterEasy',
    predicate: (r) => r.difficulty === RecipeDifficulty.Easy,
  },
  {
    key: 'rating-4up',
    label: '4★ & up',
    labelKey: 'library.filterRating4',
    predicate: (r) => (r.rating ?? 0) >= 4,
  },
  ...DIETARY_TAGS.map(({ tag, label }) => ({
    key: `dietary:${tag}`,
    label,
    labelKey: DIETARY_LABEL_KEYS[tag],
    predicate: (r: Recipe) => (r.dietary ?? []).includes(tag),
  })),
]

// Distinct tags across the given recipes, sorted for a stable pill order — the
// tag pills are derived from the recipes on screen, not a fixed list.
function uniqueTags(recipes: { tags?: string[] }[]): string[] {
  const set = new Set<string>()
  for (const r of recipes) for (const t of r.tags ?? []) set.add(t)
  return [...set].sort((a, b) => a.localeCompare(b))
}

// Owns the multi-select filter state and applies it. A recipe must match EVERY
// active filter (AND). No active filters = "All" (everything).
export function useRecipeFilter<T extends Recipe>(recipes: T[]) {
  const [activeKeys, setActiveKeys] = useState<string[]>([])

  const toggle = (key: string) =>
    setActiveKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    )
  const clear = () => setActiveKeys([])

  // Reset filters to "All" whenever this listing unmounts (i.e. the user
  // navigates away), so re-entering any listing route always starts
  // unfiltered — the web equivalent of the RN useFocusEffect blur-reset.
  useEffect(() => {
    return () => setActiveKeys([])
  }, [])

  // Static pills plus a dynamic pill per distinct tag across these recipes.
  const filters = useMemo<RecipeFilter[]>(() => {
    const tagFilters = uniqueTags(recipes).map((t) => ({
      key: `tag:${t}`,
      label: t,
      predicate: (r: Recipe) => (r.tags ?? []).includes(t),
    }))
    return [...RECIPE_FILTERS, ...tagFilters]
  }, [recipes])

  const filteredRecipes = useMemo(() => {
    const active = filters.filter((f) => activeKeys.includes(f.key))
    return active.length
      ? recipes.filter((r) => active.every((f) => f.predicate(r)))
      : recipes
  }, [recipes, activeKeys, filters])

  return { filters, activeKeys, toggle, clear, filteredRecipes }
}

export interface RecipeFilterBarProps {
  /** The pill set to render — pass the `filters` from useRecipeFilter. */
  filters: RecipeFilter[]
  activeKeys: string[]
  onToggle: (key: string) => void
  onClear: () => void
  totalCount: number
}

// Pill styling for a filter chip (active vs inactive).
const chipClass = (active: boolean) =>
  `shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${active
    ? 'bg-terracotta text-white border border-terracotta shadow-sm'
    : 'bg-surface border border-line text-ink-soft hover:bg-surface-2 hover:text-ink'
  }`

export function RecipeFilterBar({
  filters,
  activeKeys,
  onToggle,
  onClear,
  totalCount,
}: RecipeFilterBarProps) {
  const t = useT()
  const noneActive = activeKeys.length === 0

  return (
    <div className="mb-4 overflow-x-auto no-scrollbar">
      <div className="flex flex-row gap-2 w-max">
        <button className={chipClass(noneActive)} onClick={onClear}>
          {t('library.allCount', { count: totalCount })}
        </button>

        {filters.map((f) => {
          const active = activeKeys.includes(f.key)
          return (
            <button
              key={f.key}
              className={chipClass(active)}
              onClick={() => onToggle(f.key)}
            >
              {f.labelKey ? t(f.labelKey) : f.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
